import { Alert, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import DateTime from "react-datetime";
import { useEffect, useState } from "react";
import moment from "moment";
import { useMutation } from "@apollo/client";
import toast from "react-hot-toast";

import { updateOrganisationSchedule } from "../../graphql/company-policy/updateOrganisationSchedule";
import { useUserOrganisationSchedules } from "../../hooks/useOrganisation";
import { OrganisationSchedule } from "../../interfaces/models/Organisation";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import PromptDialog from "../../components/PromptDialog";
import CompanyNudgePlanKanban from "../../components/Compliance/CompanyNudgePlanKanban";
import { COMPANY_NUDGE_SCHEDULE_DAYS } from "../../helper/constants";

interface ModalParams {
  name: string;
  action: string;
}

const CompanyPolicyNudgePlannerPage = () => {
  const breadcrumbsData = [
    { name: "Complaince", path: `/compliance` },
    { name: "Nudge Planner", path: `/compliance/nudge-planner` },
  ];

  const days = COMPANY_NUDGE_SCHEDULE_DAYS;
  const dateNow = moment().format("yyyy-MM-DD");
  const inputFormat = "yyyy-MM-DD HH:mm";
  const MAX_TIMING_PER_DAY = 2;

  const { data, error, loading } = useUserOrganisationSchedules();

  const [updateOrganisationScheduleHook] = useMutation(updateOrganisationSchedule, {
    onCompleted: () => {
      toast.success(`${modal.name} Nudge Time was successful`);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [times, setTimes] = useState<OrganisationSchedule[]>([]);

  const [isOnProcess, setIsOnProcess] = useState(false);

  const [modal, setModal] = useState<ModalParams>({
    name: "Add",
    action: "add"
  });
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const [schedulerFormData, setSchedulerFormData] = useState<OrganisationSchedule>({
    _id: "",
    time: "",
    day: 1
  });

  const [isDeleteTimeModalOpen, setIsDeleteTimeModalOpen] = useState(false);

  useEffect(() => {
    if(data) {
      const rawData: OrganisationSchedule[] = data.me?.organisation?.company_policy_nudge_schedules || [];
      setTimes(rawData);
    }
  },[data]);


  //#region Add/Update Schedule

  const handleOpenAddSchedulerModal = (day: number) => {
    setSchedulerFormData({
      _id: "",
      time: "14:00",
      day: day
    });

    setModal({
      name: "Add",
      action: "add"
    });

    setIsSchedulerOpen(true);
  }

  const handleOpenEditSchedulerModal = (schedule: OrganisationSchedule) => {
    setSchedulerFormData(schedule);

    setModal({
      name: "Edit",
      action: "edit"
    });

    setIsSchedulerOpen(true);
  }

  const isScheduleAllowed = () => {
    if(!schedulerFormData.time) return false;

    if(modal.action === "add") {
      const countTimingsOnDay = times.filter(u => u.day === schedulerFormData.day);
      if(countTimingsOnDay.length >= MAX_TIMING_PER_DAY) return false;
    }

    // parse time as "HH:mm" even if input is "h:mm"
    const time = moment(schedulerFormData.time, ["H:mm", "HH:mm"], true);
    if (!time.isValid()) return false;

    const start = moment("07:00", "HH:mm");
    const end   = moment("17:00", "HH:mm");
    //Check if the time added is between 7am and 5pm
    if(!(time.isSameOrAfter(start) && time.isSameOrBefore(end))) {
      toast.error("Schedule cannot be outside 7am to 5pm");
      return false;
    }

    const getDuplicate = times.find(u => u.day === schedulerFormData.day
      && u.time === schedulerFormData.time 
      && ((schedulerFormData._id && u._id !== schedulerFormData._id) || !schedulerFormData._id) );

    if(getDuplicate) {
      toast.error(schedulerFormData.time + " is already scheduled on " + days[schedulerFormData.day - 1]);
      return false;
    }

    return true;

  }

  const handleSubmitSchedule = async () => {
    if(!isScheduleAllowed()) return;
    if(isOnProcess) return;

    setIsOnProcess(true);

    let schedule: OrganisationSchedule = { 
      _id: "", 
      day: schedulerFormData.day, 
      time: schedulerFormData.time 
    };

    if(modal.action === "edit") {
      schedule._id = schedulerFormData._id
    }

    const result = await updateOrganisationScheduleHook({
      variables: {
        schedule,
        isDelete: false
      },
    })
      .then(res => ({
        data: res.data.organisation_UpdateSchedule.company_policy_nudge_schedules,
        success: (res.errors ? false : true)
      }))
      .catch(() =>({ data: null, success: false}));
      
    setIsOnProcess(false);

    if(!result.success) return;

    setTimes(result.data);
    setIsSchedulerOpen(false);
  }

  //#endregion

  //#region Delete Schedule
  const handleOpenDeleteModal = (schedule: OrganisationSchedule) => {
    setSchedulerFormData(schedule);

    setModal({
      name: "Delete",
      action: "delete"
    });

    setIsDeleteTimeModalOpen(true);
  }

  const handleDeleteSchedule = async () => {
    if(isOnProcess) return;

    setIsOnProcess(true);

    const result = await updateOrganisationScheduleHook({
      variables: {
        schedule: {
          _id: schedulerFormData._id, // ID is only one need for deletion
          time: schedulerFormData.time,
          day: schedulerFormData.day
        },
        isDelete: true
      },
    })
      .then(res => ({
        data: res.data.organisation_UpdateSchedule.company_policy_nudge_schedules,
        success: (res.errors ? false : true)
      }))
      .catch(() =>({ data: null, success: false}));
      
    setIsOnProcess(false);

    if(!result.success) return;

    setTimes(result.data);
    setIsDeleteTimeModalOpen(false);
  }
  //#endregion

  return (
    <>
      {error && <Alert color="danger">Oops, we’re having trouble getting the schedules. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
      <Breadcrumbs crumbs={breadcrumbsData} />
        <div>
          <div>
            <small>Plan and assign nudge times for weekdays using the scheduler.</small>
          </div>
        </div>
        <CompanyNudgePlanKanban
          times={times}
          loading={loading || isOnProcess}
          hasCRUD={true}
          days={days}
          handleOpenAddSchedulerModal={handleOpenAddSchedulerModal}
          handleOpenEditSchedulerModal={handleOpenEditSchedulerModal}
          handleOpenDeleteModal={handleOpenDeleteModal}
        />
        <Modal id="scheduler-modal" backdrop={true} isOpen={isSchedulerOpen}>
          <ModalHeader toggle={() => setIsSchedulerOpen(prev => !prev)} className="pb-0">
            <p className="text-lg">{modal.name} Schedule</p>
          </ModalHeader>
          <ModalBody>
            <Form>
              <fieldset>
                <FormGroup>
                  <Label>Day</Label>
                  <Input value={days[schedulerFormData.day - 1]} disabled  />
                </FormGroup>
                <FormGroup>
                  <Label>Time *</Label>
                  <DateTime 
                      className="w-100" 
                      onChange={(date) => {
                        const reformated = moment(date).format("HH:mm");
                        setSchedulerFormData((prev) => ({...prev, time: reformated}))   
                      }}
                      timeConstraints={{
                        hours: { min: 0, max: 23, step: 1 },
                        minutes: { min: 0, max: 59, step: 15 },
                        seconds: { min: 0, max: 59, step: 60 }, // keeps seconds locked at 00
                      }}
                      dateFormat={false} 
                      timeFormat={true}  
                      initialValue={moment(dateNow + " " + schedulerFormData.time, inputFormat)}
                      inputProps={{readOnly : true ,className:"form-control bg-white"}}
                  />
                </FormGroup>
              </fieldset>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button disabled={isOnProcess} onClick={() => setIsSchedulerOpen((prev) => !prev)}> Close </Button>
            <Button disabled={isOnProcess} onClick={handleSubmitSchedule} color="primary"> Save </Button>
          </ModalFooter>
        </Modal>

        <PromptDialog
          isOpen={isDeleteTimeModalOpen}
          onCancel={() => setIsDeleteTimeModalOpen(false)}
          onConfirm={handleDeleteSchedule}
          title={`Delete Schedule`}
          promptMessage="Action is Irreversible, schedule will be removed permanently"
          confirmBtnTxt={"Delete"}
          isOnProcess={isOnProcess}
        />
    </>
  );
};

export { CompanyPolicyNudgePlannerPage as CompanyPolicyNudgePlanner };